import DemoNotice from './DemoNotice'
import BackRow from './BackButton'
import { useCallback, useContext, useEffect, useState } from 'react'
import Account from '../components/Account'
import Icon, { type IconName } from '../components/Icon'
import type { User } from '../lib/auth'
import SignOut from '../components/SignOut'
import { categoryIcon } from './categories'
import { worx, type Category, type Employer, type JobCard, type Membership } from './client'
import DeleteAccount from './DeleteAccount'
import { AdminSection } from './AccountPanel'
import { AddRole, FindProCard, RolesContext } from './roles'
import Feedback from './Feedback'
import { Card, Logo } from './Jobs'
import { categoryName, tw } from './strings'
import { done as savedSound } from './feel'

/** Matches the CHECK constraint in 0106 and TAGLINE_MAX on the server. */
const TAGLINE_MAX = 48

type Pane = null | 'contact' | 'about' | 'tagline' | 'team' | 'account' | 'public' | 'help' | 'feedback'

/**
 * The business, as the people who run it see it.
 *
 * ── Rows, not a form ────────────────────────────────────────────────────────
 *
 * Everything here used to be one long edit form plus core's account panel
 * stacked underneath it. That is the right shape for filling something in once
 * and the wrong shape for coming back: an employer opening this screen is
 * checking one thing — the phone number, who else can post, whether they are
 * verified yet — and a form makes them read all of it to find the one.
 *
 * So the screen is what it actually is: a card that says who you are, and two
 * groups of rows that each say their own answer on the face of them. Only the
 * row you tap opens.
 *
 * ── Why the public profile is a pane and not a route ────────────────────────
 *
 * "View Public Profile" answers a question an employer asks about THIS screen
 * — what do applicants see of us — so it opens over it and closes back to it.
 * A route would put the answer somewhere they have to navigate back from, and
 * there is nothing else on the island that links to it.
 */
export default function BusinessScreen({
  membership,
  user,
  onSwitchToWorker,
  onSignedOut,
}: {
  membership: Membership
  user: User
  onSwitchToWorker: () => void
  onSignedOut: () => void | Promise<void>
}) {
  const roleState = useContext(RolesContext)
  const [employer, setEmployer] = useState<Employer | null>(null)
  const [categories, setCategories] = useState<Category[]>([])
  const [pane, setPane] = useState<Pane>(null)

  const load = useCallback(async () => {
    try {
      setEmployer((await worx.employer(membership.employerId)).employer)
    } catch {
      setEmployer(null)
    }
  }, [membership.employerId])

  useEffect(() => { void load() }, [load])
  useEffect(() => { void worx.categories().then((c) => setCategories(c.categories)).catch(() => {}) }, [])

  if (!employer) return <p className="muted">…</p>

  const category = categories.find((c) => c.id === employer.categoryId)
  const owner = employer.role === 'owner'
  const back = () => { setPane(null); void load() }

  if (pane === 'public') return <PublicProfile employer={employer} category={category} onBack={back} />
  if (pane === 'account') {
    return (
      <div className="w-stack">
        <BackLink onBack={back} />
        <Account user={user} />
        <SignOut onSignedOut={onSignedOut} />
        <DeleteAccount onDeleted={onSignedOut} />
        <AdminSection user={user} />
      </div>
    )
  }
  if (pane === 'feedback') {
    return (
      <div className="w-stack">
        <BackLink onBack={back} />
        <Feedback />
      </div>
    )
  }
  if (pane && pane !== 'help') {
    return <EditPane pane={pane} employer={employer} categories={categories} onBack={back} />
  }

  return (
    <div className="w-feed">
      <h1 className="w-feed-heading">{tw('w.biz.screenTitle')}</h1>
      {employer.isDemo && <DemoNotice practice />}

      <section className="w-bizcard">
        <Logo name={employer.name} src={employer.logoId} className="w-bizcard-logo" />

        <div className="w-bizcard-id">
          <h2 className="w-bizcard-name">{employer.name}</h2>
          {category && (
            <p className="w-cfact">
              <span className="w-cfact-ic"><Icon name={categoryIcon(category.id)} size={16} /></span>
              {categoryName(category)}
            </p>
          )}
          {employer.location && (
            <p className="w-cfact">
              <span className="w-cfact-ic"><Icon name="pin" size={16} /></span>
              {employer.location}
            </p>
          )}
          {/* Verified is a fact about the business and the only badge on this
              screen. Pending is shown with the same weight, because "we are
              still waiting" is the thing an unverified employer most wants to
              know and the reason their postings are not out yet. */}
          <span className={employer.verified ? 'w-verify' : 'w-verify w-verify-wait'}>
            <Icon name={employer.verified ? 'check' : 'clock'} size={15} />
            {employer.verified ? tw('w.biz.verified2') : tw('w.biz.pendingReview')}
          </span>
        </div>

        <div className="w-bizcard-do">
          <button className="w-cta w-cta-quiet" onClick={() => setPane('public')}>
            <Icon name="eye" size={18} /> {tw('w.biz.viewPublic')}
          </button>
          {owner && (
            <button className="w-cta" onClick={() => setPane('contact')}>
              <Icon name="pencil" size={18} /> {tw('w.biz.edit')}
            </button>
          )}
        </div>
      </section>

      <h2 className="w-group-head">{tw('w.biz.details')}</h2>
      <div className="w-rows">
        <Row
          icon="phone"
          title={tw('w.biz.contactRow')}
          sub={[employer.phone, employer.email].filter(Boolean).join(' · ') || tw('w.biz.contactRowEmpty')}
          onClick={owner ? () => setPane('contact') : undefined}
        />
        <Row
          icon="document"
          title={tw('w.biz.aboutRow')}
          sub={employer.aboutEn || employer.aboutEs || tw('w.biz.aboutRowEmpty')}
          onClick={owner ? () => setPane('about') : undefined}
        />
        <Row
          icon="megaphone"
          title={tw('w.biz.taglineRow')}
          sub={employer.tagline || tw('w.biz.taglineRowEmpty')}
          onClick={owner ? () => setPane('tagline') : undefined}
        />
      </div>

      <h2 className="w-group-head">{tw('w.biz.account')}</h2>
      <div className="w-rows">
        <Row
          icon="people"
          title={tw('w.biz.teamRow')}
          sub={`${employer.members ?? 1} ${(employer.members ?? 1) === 1 ? tw('w.biz.admin1') : tw('w.biz.adminMany')}`}
          onClick={() => setPane('team')}
        />
        <Row
          icon="bell"
          title={tw('w.biz.accountRow')}
          sub={tw('w.biz.accountRowSub')}
          onClick={() => setPane('account')}
        />
        <Row
          icon="bulb"
          title={tw('w.tab.feedback')}
          sub={tw('w.fb.title')}
          onClick={() => setPane('feedback')}
        />
        {/* The same switch as the text one above the screen. It is here too
            because this is where somebody looks for "I am wearing the wrong
            hat", and a control you have to remember is up there is one you do
            not find. */}
        {/* Only for an account that actually holds the worker role: a business
            account cannot become one (shared/roles.ts), so for everyone else
            this row would lead to a refusal. */}
        {roleState?.roles.includes('worker') && (
          <Row
            icon="switch"
            title={tw('w.biz.switchRow')}
            sub={tw('w.biz.switchRowSub')}
            onClick={onSwitchToWorker}
          />
        )}
      </div>

      <FindProCard />
      <AddRole />

      {/* Coral, because it is the one thing on this screen that reaches a
          person rather than a record. */}
      <button className="w-help" onClick={() => setPane('help')}>
        <span className="w-help-ic"><Icon name="help" size={18} /></span>
        <span className="w-help-label">{tw('w.biz.help')}</span>
        <Icon name="chevron" size={18} />
      </button>

      {pane === 'help' && <HelpSheet employerId={employer.id} onClose={() => setPane(null)} />}
    </div>
  )
}

function BackLink({ onBack }: { onBack: () => void }) {
  return (
    <BackRow onBack={onBack} />
  )
}

/**
 * One row: a picture, what it is, and what it currently says.
 *
 * The sub-line is the VALUE, not a description of the field. "787-555-0142 ·
 * hola@…" answers the question the row asks; "your phone and email" makes
 * somebody open it to find out whether it is filled in.
 */
function Row({
  icon, title, sub, onClick,
}: {
  icon: IconName
  title: string
  sub: string
  onClick?: () => void
}) {
  return (
    <button className="w-row" onClick={onClick} disabled={!onClick}>
      <span className="w-row-ic"><Icon name={icon} size={20} /></span>
      <span className="w-row-text">
        <span className="w-row-title">{title}</span>
        <span className="w-row-sub">{sub}</span>
      </span>
      {onClick && <Icon name="chevron" size={20} className="w-row-go" />}
    </button>
  )
}

/**
 * One row's editor.
 *
 * Three panes out of one component rather than three forms: they differ by
 * which fields they show, and every one of them is the same PATCH with the
 * same "Saved." afterwards. A per-pane form is three places to get the
 * error-handling subtly different.
 */
function EditPane({
  pane, employer, categories, onBack,
}: {
  pane: Exclude<Pane, null | 'public' | 'account' | 'help' | 'feedback'>
  employer: Employer
  categories: Category[]
  onBack: () => void
}) {
  const [form, setForm] = useState({
    name: employer.name,
    categoryId: employer.categoryId ?? '',
    contactName: employer.contactName ?? '',
    phone: employer.phone ?? '',
    email: employer.email ?? '',
    location: employer.location ?? '',
    aboutEn: employer.aboutEn ?? '',
    aboutEs: employer.aboutEs ?? '',
    tagline: employer.tagline ?? '',
  })
  const [busy, setBusy] = useState(false)
  const [done, setDone] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const set = (k: keyof typeof form) => (e: { target: { value: string } }) => {
    setForm((f) => ({ ...f, [k]: e.target.value }))
    setDone(false)
  }

  async function save(e: React.FormEvent) {
    e.preventDefault()
    setBusy(true)
    setError(null)
    try {
      await worx.updateEmployer(employer.id, form)
      savedSound()
      setDone(true)
    } catch (err) {
      setError(err instanceof Error ? err.message : tw('w.error.save'))
    } finally {
      setBusy(false)
    }
  }

  const titles = {
    contact: tw('w.biz.contactRow'),
    about: tw('w.biz.aboutRow'),
    tagline: tw('w.biz.taglineRow'),
    team: tw('w.biz.teamRow'),
  }

  if (pane === 'team') {
    return (
      <div className="w-stack">
        <BackLink onBack={onBack} />
        <section className="card w-fields">
          <h3>{titles.team}</h3>
          <p className="muted">
            {employer.members ?? 1}{' '}
            {(employer.members ?? 1) === 1 ? tw('w.biz.admin1') : tw('w.biz.adminMany')}
          </p>
          {/* Honest about what is not built: inviting a second person to a
              business is a real feature with a real invitation flow behind it,
              and a button here that opened nothing would be worse than a
              sentence saying so. */}
          <p className="muted small">{tw('w.biz.teamSoon')}</p>
        </section>
      </div>
    )
  }

  return (
    <form className="w-stack" onSubmit={save}>
      <BackLink onBack={onBack} />
      <section className="card w-fields">
        <h3>{titles[pane]}</h3>

        {pane === 'contact' && (
          <>
            <label>
              <span className="w-label">{tw('w.biz.name')}</span>
              <input value={form.name} onChange={set('name')} maxLength={120} required />
            </label>
            <label>
              {tw('w.biz.category')}
              <select value={form.categoryId} onChange={set('categoryId')}>
                <option value="">—</option>
                {categories.map((c) => <option key={c.id} value={c.id}>{categoryName(c)}</option>)}
              </select>
            </label>
            <label>
              {tw('w.biz.location')}
              <input value={form.location} onChange={set('location')} maxLength={200} placeholder="Esperanza, Vieques" />
            </label>
            <label>
              {tw('w.biz.contact')}
              <input value={form.contactName} onChange={set('contactName')} maxLength={120} />
            </label>
            <label>
              {tw('w.biz.phone')}
              <input type="tel" value={form.phone} onChange={set('phone')} maxLength={40} />
            </label>
            <label>
              {tw('w.biz.email')}
              <input type="email" value={form.email} onChange={set('email')} maxLength={200} />
            </label>
          </>
        )}

        {pane === 'about' && (
          <>
            <label>
              English
              <textarea value={form.aboutEn} onChange={set('aboutEn')} rows={5} maxLength={2000} />
            </label>
            <label>
              Español
              <textarea value={form.aboutEs} onChange={set('aboutEs')} rows={5} maxLength={2000} />
            </label>
          </>
        )}

        {pane === 'tagline' && (
          <label>
            {tw('w.biz.tagline')}
            <input value={form.tagline} onChange={set('tagline')} maxLength={TAGLINE_MAX} />
            {/* The counter is not decoration: the cap is a LAYOUT limit — one
                row on every job card — so somebody has to be able to see
                themselves approaching it rather than being cut off at save. */}
            <span className="muted small">
              {tw('w.biz.taglineHint')} · {form.tagline.length}/{TAGLINE_MAX}
            </span>
          </label>
        )}

        {error && <p className="error">{error}</p>}
        <button className="primary" type="submit" disabled={busy}>
          {busy ? '…' : tw('w.biz.save')}
        </button>
        {done && <p className="w-ok">{tw('w.biz.saved')}</p>}
      </section>
    </form>
  )
}

/** Exactly what a worker sees, on the employer's own screen. */
function PublicProfile({
  employer, category, onBack,
}: {
  employer: Employer
  category?: Category
  onBack: () => void
}) {
  const [jobs, setJobs] = useState<JobCard[] | null>(null)
  useEffect(() => {
    void worx.employerJobs(employer.id)
      .then((r) => setJobs(r.jobs.filter((j) => j.status === 'published')))
      .catch(() => setJobs([]))
  }, [employer.id])

  return (
    <div className="w-stack">
      <BackLink onBack={onBack} />
      <p className="muted small">{tw('w.biz.publicTitle')}</p>

      <section className="w-bizcard">
        <Logo name={employer.name} src={employer.logoId} className="w-bizcard-logo" />
        <div className="w-bizcard-id">
          <h2 className="w-bizcard-name">{employer.name}</h2>
          {category && (
            <p className="w-cfact">
              <span className="w-cfact-ic"><Icon name={categoryIcon(category.id)} size={16} /></span>
              {categoryName(category)}
            </p>
          )}
          {employer.location && (
            <p className="w-cfact">
              <span className="w-cfact-ic"><Icon name="pin" size={16} /></span>
              {employer.location}
            </p>
          )}
          {employer.verified && (
            <span className="w-verify"><Icon name="check" size={15} /> {tw('w.biz.verified2')}</span>
          )}
        </div>
        {employer.tagline && <p className="w-bizcard-tagline">{employer.tagline}</p>}
      </section>

      {(employer.aboutEn || employer.aboutEs) && (
        <section className="card">
          <p className="w-prose">{employer.aboutEn || employer.aboutEs}</p>
        </section>
      )}

      <h2 className="w-group-head">{tw('w.biz.openJobs')}</h2>
      {jobs === null && <p className="muted">…</p>}
      {jobs?.length === 0 && <p className="muted w-empty">{tw('w.post.none')}</p>}
      {jobs?.map((j) => <Card key={j.id} job={j} />)}
    </div>
  )
}

/**
 * Get Help.
 *
 * It files a report with `subjectType: 'support'`, which lands in the queue an
 * administrator already watches and already gets notified about. A second
 * table called `help_requests` would have been the same three columns and a
 * second place to forget to look.
 */
function HelpSheet({ employerId, onClose }: { employerId: string; onClose: () => void }) {
  const [text, setText] = useState('')
  const [busy, setBusy] = useState(false)
  const [sent, setSent] = useState(false)

  async function send() {
    if (!text.trim()) return
    setBusy(true)
    try {
      await worx.report({ subjectType: 'support', subjectId: employerId, reason: 'help', detail: text })
      setSent(true)
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      <div className="w-scrim" onClick={onClose} aria-hidden="true" />
      <div className="w-sheet" role="dialog" aria-modal="true" aria-label={tw('w.biz.help')}>
        <header>
          <h2>{tw('w.biz.helpTitle')}</h2>
          <button className="link" onClick={onClose}>{tw('w.feed.done')}</button>
        </header>
        {sent ? (
          <p className="w-ok">{tw('w.biz.helpSent')}</p>
        ) : (
          <>
            <p className="muted small">{tw('w.biz.helpHint')}</p>
            <textarea rows={5} value={text} onChange={(e) => setText(e.target.value)} maxLength={2000} />
            <button className="primary" disabled={busy || !text.trim()} onClick={() => void send()}>
              <Icon name="mail" size={18} /> {busy ? '…' : tw('w.biz.helpSend')}
            </button>
          </>
        )}
      </div>
    </>
  )
}
